import { createSlice } from "@reduxjs/toolkit";

const hasAnswer = (values) => {
  const answer = values?.answer;
  if (answer === undefined || answer === null || answer === "") return false;
  if (Array.isArray(answer)) return answer.length > 0;
  return true;
};

// Single place that moves an answer out of the form and into savedAnswers.
// Previously this lived inside nextQuestion, which is why an answer only survived
// if the student left the question using the Next button: leapQuestion (clicking a
// question number) and prevQuestion moved the pointer without ever writing here, so
// the answer was silently dropped and never reached the submission.
const persistAnswer = (state, values) => {
  if (!hasAnswer(values)) return false;

  state.questions[state.currentQuestion] = values;
  state.savedAnswers[state.currentQuestion] = {
    questionId: values.id,
    answer: values.typeId !== 2 ? values.answer.toString() : null,
    multiAnswer:
      values.typeId === 2 ? values.answer.map((i) => i.toString()) : [],
    questionIndex: state.currentQuestion,
  };
  state.skippedQuestions = state.skippedQuestions.filter(
    (i) => i !== state.currentQuestion + 1
  );
  return true;
};

const initialState = {
  id: 0,
  userID: null,
  title: "",
  subjectName: "",
  gradeName: "",
  currentQuestion: 0,
  currentData: {
    id: 0,
    text: "",
    typeId: 0,
    rank: 0,
    answer: [],
    hint: "",
    imagePath: null,
    answers: null,
    duration: 5,
    head: null,
    columns: 0,
  },
  totalTime: 100,
  questions: [],
  savedAnswers: [],
  timers: [0],
  initialTimes: [0],
  results: {},
  skippedQuestions: [],
  price: 0,
};

const assessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    saveID: (state, action) => {
      state.userID = action.payload;
    },
    loadData: (state, action) => {
      state.id = action.payload?.id;
      state.price = action.payload?.price;
      state.title = action.payload?.localizedName;
      state.gradeName = action.payload?.localizedGradeName;
      state.subjectName = action.payload?.localizedSubjectName;
      // CR-03: the exam ran on maxDuration, but Duration is the field the admin fills in
      // and the one the exam card advertises - they disagree on 348 of the 418 exams, so
      // students were being given a different length from the one they were shown.
      // maxDuration stays as the fallback for any record with no Duration of its own.
      const totalMinutes =
        action.payload?.duration || action.payload?.maxDuration || 0;
      state.totalTime = totalMinutes * 60;
      state.timers = action.payload?.questions.map((q) => q.duration * 60);
      state.initialTimes = action.payload?.questions.map(
        (q) => q.duration * 60
      );
      state.initialTimes.push(totalMinutes * 60);
    },
    loadQuestions: (state, action) => {
      state.questions = action.payload;
      state.currentData = action.payload[0];
    },
    // Writes the current question's answer into savedAnswers - the only array the
    // submission actually sends. Shared by the autosave and by nextQuestion so both
    // produce an identical shape. Returns whether anything was stored.
    saveAnswer: (state, action) => {
      persistAnswer(state, action.payload);
    },
    nextQuestion: (state, action) => {
      if (!persistAnswer(state, action.payload)) {
        if (!state.skippedQuestions.includes(state.currentQuestion + 1)) {
          state.skippedQuestions.push(state.currentQuestion + 1);
        }
      }

      if (state.currentQuestion < state.questions.length - 1) {
        state.currentData = state.questions[state.currentQuestion + 1];
      }
      state.currentQuestion += 1;
    },
    prevQuestion: (state) => {
      state.currentData = state.questions[state.currentQuestion - 1];
      state.currentQuestion -= 1;
    },
    leapQuestion: (state, action) => {
      if (
        !state?.currentData?.answer &&
        state.currentQuestion < state.questions.length
      ) {
        if (!state.skippedQuestions.includes(state.currentQuestion + 1)) {
          state.skippedQuestions.push(state.currentQuestion + 1);
        }
      }
      state.currentQuestion = action.payload;
      state.currentData = state.questions[action.payload];
    },
    setTimer: (state, action) => {
      state.timers[state.currentQuestion] = action.payload;
    },
    tick: (state) => {
      if (state.timers[state.currentQuestion] > 0) {
        state.timers[state.currentQuestion] -= 1;
      }
    },
    saveResults: (state, action) => {
      state.results = action.payload;
    },
  },
});

export const {
  saveAnswer,
  nextQuestion,
  leapQuestion,
  prevQuestion,
  setTimer,
  tick,
  loadData,
  loadQuestions,
  saveResults,
  saveID,
} = assessmentSlice.actions;

export default assessmentSlice.reducer;
